<template>
  <button
    class="grid-x align-center bag-icon"
    title="Shopping bag"
    :class="[iconClass, { 'bag-filled': !isEmpty, 'is-minimal': isMinimal }]"
    :style="iconStyle"
    :aria-label="ariaLabel"
    data-testid="bag-icon-link"
    @mouseenter="onHover"
    @click="onClick"
  >
    <div v-if="!isEmpty" class="grid-x align-center full-height" :class="isMcom ? 'align-middle' : 'align-bottom'">
      <span class="counter small">{{ count }}</span>
    </div>
  </button>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { Links } from 'config';

import { useEnvProps } from '~/stores/envProps';
import { useNavQuickBag } from '~/stores/navQuickBag';

import { useBag } from '~/composables/dumb/useBag';
import { useBackboneEvents } from '~/composables/dumb/useBackboneEvents';
import events from '#shared/constants/mcom/events';
import { SET_COUNT, INCREASE_COUNT } from '#shared/types/actions';
import { GET_IS_EMPTY, GET_FORMATTED_COUNT, GET_IS_MORE_THAN_ONE } from '#shared/types/getters';
import getAssetImage from '~/utils/getAssetImage';
import sendTrackParams from '#shared/features/analytics/sendTrackParams';

defineProps({
  isMinimal: {
    type: Boolean,
    default: false,
  },
});

const pageData = usePageData();
const { isMcom } = useEnvProps();
const navQuickBag = useNavQuickBag();

const NUMBERED_BAG_SELECTOR = computed(() => {
  return isMcom ? 'bag-mcom-with-star' : 'numbered-bag';
});
const UN_NUMBERED_BAG_SELECTOR = isMcom ? 'bag-mcom-with-star' : 'bag-logo-brown';

const isEmpty = computed(() => navQuickBag?.[GET_IS_EMPTY]);
const count = computed(() => navQuickBag?.[GET_FORMATTED_COUNT]);
const isMoreThanOne = computed(() => navQuickBag?.[GET_IS_MORE_THAN_ONE]);
const iconClass = computed(() => (isEmpty.value ? UN_NUMBERED_BAG_SELECTOR : NUMBERED_BAG_SELECTOR.value));
const ariaLabel = computed(() => `${count.value} item${isMoreThanOne.value ? 's' : ''} in bag`);
const iconStyle = computed(() => (!isEmpty.value && !isMcom ? { backgroundImage: `url('${getAssetImage('numberedBag.svg')}')` } : {}));
const isHovered = ref(false);

function setBagCount(bagCount) {
  navQuickBag[SET_COUNT](bagCount);
}

function increaseBagCount(bagCount) {
  navQuickBag[INCREASE_COUNT](bagCount);
}

function onHover() {
  if (!isHovered.value) {
    isHovered.value = true;
  }
}

function onClick() {
  try {
    const { context } = pageData.navigation;
    sendTrackParams(Links.bag.tracking, context);
  } catch (error) {
    console.error('Bag Click Analytics Failed', error);
  } finally {
    window.location.href = Links.bag.url;
  }
}

onMounted(() => {
  const { getBagCountCookie } = useBag();
  const { listenTo } = useBackboneEvents();
  const bagCount = getBagCountCookie();

  setBagCount(bagCount);
  listenTo(events.bag.setBagCount, setBagCount);
  listenTo(events.bag.increaseBagCount, increaseBagCount);
});

onUnmounted(() => {
  const { stopListening } = useBackboneEvents();

  stopListening(events.bag.setBagCount, setBagCount);
  stopListening(events.bag.increaseBagCount, increaseBagCount);
});
</script>

<style lang="scss" scoped>
.bag-icon {
  cursor: pointer;
  flex-shrink: 0;
  background-position: center;
  transform: translate3d(0, 0, 0); // HFR-1045: hack for Safari to render correctly instead of pixelated background image
  position: relative; // Establish a positioning context for the counter
}
</style>

<style lang="scss" brand="mcom" scoped>
.bag-icon {
  .counter {
    display: flex;
    justify-content: center;
    align-items: center;
    position: absolute;
    border-radius: 64px;
    width: 17px;
    height: 16px;
    background: $primary-color;
    top: -6px;
    right: -6px;
    z-index: 10;
    font-size: 10px;
    font-weight: 400;
    margin-top: 5px;
    color: $white;
  }

  &:not(.is-minimal) .counter {
    @include breakpoint(large up) {
      top: -2px;
    }
  }
}
</style>

<style lang="scss" brand="bcom" scoped>
.bag-icon {
  width: 24px;
  height: 24px;
  background-size: 24px 24px;
  display: block;
  background-repeat: no-repeat;

  &-large {
    width: 28px;
    height: 28px;
    background-size: 28px 28px;

    .counter {
      margin-top: 0;
      position: relative;
      top: -2px;
    }
  }

  @include breakpoint(large up) {
    width: 28px;
    height: 28px;
    background-size: 28px 28px;
  }
}

.counter {
  display: block;
  font-weight: 700;
  color: $secondary-color;

  @include breakpoint(large up) {
    margin-bottom: 2px;
  }
}

.rail-btn-hover {
  .counter {
    margin-top: 0;
    position: relative;
    top: -6px;
  }
}
</style>
